import { Box, Heading } from '@chakra-ui/layout'
import {
  Alert,
  AlertDescription,
  AlertIcon, Text as ChakraText,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { CreditBalanceDetails } from '../../components/Billing/CreditBalance'
import type {
  CreditBalanceApiResponse
} from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'


const SubscriptionsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const { isOpen, onClose, onOpen } = useDisclosure()

  const [canAddCredit, setCanAddCredit] = useState(false)

  const { data, request, loading } = useFetch<CreditBalanceApiResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber && user.billingDetails.billTo === 'self') {
      const data = (await request.get(
        `/api/billing/credit-balance/${user.clientNumber}`,
      )) as CreditBalanceApiResponse | undefined

      if (!data) {
        return;
      }

      setCanAddCredit(data.canAddCredit)
      return;
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  if (user?.billingDetails.billTo !== 'self') {
    return (
      <>
        <MainContentHeadingBox />
        <MainContentWrapper>
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              {t(
                'billing_managed_by_parent_description',
                "This account's billing is managed by the parent agency account. If you want to add credit balance in parent account, please contact the parent account owner.",
              )}
            </AlertDescription>
          </Alert>
        </MainContentWrapper>
      </>
    )
  }

  return (
    <>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
          lineHeight="3rem"
        >
          &nbsp;
        </Heading>
      </MainContentHeadingBox>
      <Box padding="1.5em">
        <VStack
          px={8}
          py={6}
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing="1em"
        >
          {!loading && data ? (
            <CreditBalanceDetails data={data} refreshBalance={() => {
              void getData();
            }} />
          ) : null}
          {!loading && !data ? (
            <ChakraText marginBottom="1em" color={colors.appSlate}>
              {t(
                'error_loading_subscription_details',
                'Error in loading subscription details. Please contact support.',
              )}
            </ChakraText>
          ) : null}
          {loading ? (
            <ChakraText marginBottom="1em" color={colors.appSlate}>
              {t('loading', 'Loading...')}
            </ChakraText>
          ) : null}
        </VStack>
      </Box>
    </>
  )
}

export default SubscriptionsLayout
